import styles from "./Modals.module.scss";
import React, { useEffect, useMemo, useRef, useState } from "react";
import type { azureClientSecretSettings, azureStorageSharedKeySettings, azureSasUrlSettings, Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { Field, FieldProps, FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { useTranslation } from "react-i18next";
import { FormCheckBox } from "@whatslly/ui-components/v2/FormElements/FormCheckBox";
import classNames from "classnames";
import { useDependencyInjector, useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import { ButtonSet } from "@whatslly/ui-components/v2/BaseElements/ButtonSet";
import pick from "lodash/pick";
import isURL from "validator/es/lib/isURL";
import { FormPasswordTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput/FormPasswordTextInput";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { DESTINATIONS_FILENAME_TEMPLATES } from "@whatslly/commons/destinationsFileNameTemplates";
import { CheckBox } from "@whatslly/ui-components/v2/BaseElements/CheckBox";
import { TextInputWithCopy } from "@whatslly/ui-components/v2/TextInputWithCopy";
import type { DestinationFormProps } from "../utils/DestinationsListTypes";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";

type AzureBlobDestination = Extract<Destination, { type: "azureBlob" }>;

const generateDek = async (): Promise<string> => {
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  const raw = new Uint8Array(await crypto.subtle.exportKey("raw", key));
  let s = "";
  for (let i = 0; i < raw.length; i++) s += String.fromCharCode(raw[i]);
  return btoa(s);
};

const FILENAME_TEMPLATE = '{{orgId}}/{{sourcePhone}}/{{date timestamp "YYYY/MM/DD"}}.';
const FILENAME_TEMPLATE_OPTIONS = DESTINATIONS_FILENAME_TEMPLATES.map((template) => ({ label: template, value: template }));

type AvailableTabs = "storage-shared-key-credential" | "client-secret-credential" | "sas-url-credential";

type azureStorageSharedKeySettingsFormDataTypeFields =
  | "accountName"
  | "accountKey"
  | "storeMedia"
  | "containerName"
  | "format"
  | "filenameTemplate"
  | "mediaFilenameTemplate"
  | "dek";
type azureStorageSharedKeySettingsFormDataType = Pick<azureStorageSharedKeySettings, azureStorageSharedKeySettingsFormDataTypeFields> & {
  dekEnabled?: boolean;
};

const getServerOrigin = (serverUrl: string) => {
  try {
    const fullUrl = serverUrl.startsWith("http://") || serverUrl.startsWith("https://") ? serverUrl : `https://${serverUrl}`;
    if (!isURL(fullUrl)) {
      return undefined;
    }
    return new URL(fullUrl).origin;
  } catch {
    return undefined;
  }
};

const CommonAzure: React.FC<{
  formatValue: string;
  settings:
    | ((azureSasUrlSettingsFormDataType | azureStorageSharedKeySettingsFormDataType | azureClientSecretSettingsFormDataType) & { type?: string })
    | undefined;
  formikContext: React.RefObject<FormikProps<any> | null>;
  selectedType: string;
  filenameTemplateSelect: { label: string; value: string } | undefined;
}> = ({ formatValue, settings, formikContext, selectedType, filenameTemplateSelect }) => {
  const { t } = useTranslation();
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const isSuperAdmin = useMemo(
    () => userStore.user?.isSuperAdmin || userStore.user?.isAdmin,
    [userStore.user?.isAdmin, userStore.user?.isSuperAdmin]
  );

  const onFileNameTemplateChange = (value: string) => {
    const newMediaFilenameTemplatePrefix = value?.startsWith("{{sourcePhone}}") ? "" : "{{orgId}}/";
    formikContext.current?.setFieldValue("mediaFilenameTemplate", `${newMediaFilenameTemplatePrefix}{{sourcePhone}}/media/{{_id}}.{{fileType}}`);
  };

  return (
    <>
      <div className={classNames(styles.field, { [styles.hidden]: !isSuperAdmin })}>
        <Label className={styles.filedLabel} text={t("destinations_page.file_name_template")} required />
        <Select
          options={FILENAME_TEMPLATE_OPTIONS.filter((opt) => {
            return opt.value.includes(formatValue ?? "csv");
          })}
          value={filenameTemplateSelect}
          paddingInline="none"
          onChange={(selectedOption: (typeof FILENAME_TEMPLATE_OPTIONS)[number]) => {
            formikContext.current?.setFieldValue("filenameTemplate", selectedOption?.value);
            onFileNameTemplateChange(selectedOption?.value);
          }}
          defaultValue={
            FILENAME_TEMPLATE_OPTIONS.find((opt) =>
              settings?.filenameTemplate ? settings.filenameTemplate === opt.value : opt.value.includes(formatValue ?? "csv")
            ) ?? {
              label: `${FILENAME_TEMPLATE}csv`,
              value: `${FILENAME_TEMPLATE}csv`,
            }
          }
        />
        <div className={classNames(styles.field, styles.hidden)}>
          <FormTextInput name={"filenameTemplate"} paddingInline="none" />
        </div>
      </div>

      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.store_media")} />
        <FormCheckBox name={"storeMedia"} paddingInline="none" />
      </div>
      <div className={classNames(styles.field, styles.fieldCheckbox)}>
        <Label className={styles.filedLabel} text={t("destinations_page.client_encryption")} />
        <Field
          name={"dekEnabled"}
          children={(
            fieldData: FieldProps<
              string,
              azureSasUrlSettingsFormDataType | azureStorageSharedKeySettingsFormDataType | azureClientSecretSettingsFormDataType
            >
          ) => {
            return (
              <div>
                <CheckBox
                  disabled={settings && settings.type === selectedType}
                  name="dekEnabled"
                  paddingInline="none"
                  checked={Boolean(fieldData.form?.values["dek"])}
                  onChange={async (e) => {
                    if (!e.target.checked) {
                      formikContext.current?.setFieldValue("dek", "");
                      return;
                    }

                    const dek = await generateDek();
                    formikContext.current?.setFieldValue("dek", dek);
                    return;
                  }}
                />
              </div>
            );
          }}
        ></Field>
      </div>
      {settings && settings.type === selectedType ? null : (
        <Field
          name={`dek`}
          children={(
            fieldData: FieldProps<
              string,
              azureSasUrlSettingsFormDataType | azureStorageSharedKeySettingsFormDataType | azureClientSecretSettingsFormDataType
            >
          ) => {
            return (
              fieldData.form?.values["dek"] && (
                <div className={styles.field}>
                  <Label className={styles.filedLabel} text={t("destinations_page.client_encryption_key")} />
                  <TextInputWithCopy type="password" value={fieldData.form?.values["dek"]} disabled={true} />
                </div>
              )
            );
          }}
        />
      )}
      <div className={classNames(styles.field, { [styles.hidden]: !isSuperAdmin })}>
        <Label className={styles.filedLabel} text={t("destinations_page.media_file_name_template")} />
        <FormTextInput name={"mediaFilenameTemplate"} paddingInline="none" disabled />
      </div>
    </>
  );
};

const StorageSharedKeyCredentialTab: React.FC<DestinationFormProps<"azureBlob">> = ({ settings, onSubmitForm }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<azureStorageSharedKeySettingsFormDataType>>(null);
  const [formatValue, setFormatValue] = React.useState<"json" | "csv">(settings?.format ?? "csv");
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const [filenameTemplateSelect, setFilenameTemplateSelect] = useState<{ label: string; value: string } | undefined>();

  const validate = async (values: azureStorageSharedKeySettingsFormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof azureStorageSharedKeySettingsFormDataType, string>> = {};
    if (!values.format) errors.format = t("field_is_required");
    if (!values.accountName) errors.accountName = t("field_is_required");
    if (!values.accountKey) errors.accountKey = t("field_is_required");
    if (!values.containerName) errors.containerName = t("field_is_required");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);

    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as azureStorageSharedKeySettingsFormDataType;
    delete data.dekEnabled;
    return data;
  };

  const getSubmitData = (): AzureBlobDestination => {
    return {
      integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
      type: "azureBlob" as const,
      settings: {
        type: "storage-shared-key-credential",
        ...getFormValues(),
        format: formatValue,
      },
    };
  };

  const onSubmit = async () => {
    store.testConnection(t, getSubmitData());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getSubmitData());
    }
  };

  const onChangeFormat = (value: "json" | "csv") => {
    setFormatValue(value);
    const currentFileNameTemplateValue = formikContext.current?.values.filenameTemplate || `${FILENAME_TEMPLATE}.${value}`;
    const [currentValue] = currentFileNameTemplateValue.split(".");
    const newFileNameTemplateValue = FILENAME_TEMPLATE_OPTIONS.find((opt) => {
      const [optValue, optFormat] = opt.value.split(".");
      return optValue === currentValue && optFormat === value;
    }) || {
      label: `${FILENAME_TEMPLATE}.${value}`,
      value: `${FILENAME_TEMPLATE}.${value}`,
    };
    setFilenameTemplateSelect(newFileNameTemplateValue);
    formikContext.current?.setFieldValue("filenameTemplate", newFileNameTemplateValue?.value || `${FILENAME_TEMPLATE}.${value}`);
  };

  useEffect(() => {
    validate(getFormValues());
  }, []);

  return (
    <FormElement
      initialValues={
        settings && (settings.type === "storage-shared-key-credential" || !settings.type)
          ? pick<azureStorageSharedKeySettings, azureStorageSharedKeySettingsFormDataTypeFields>(settings, [
              "accountName",
              "accountKey",
              "storeMedia",
              "containerName",
              "format",
              "filenameTemplate",
              "mediaFilenameTemplate",
              "dek",
            ])
          : {
              format: "csv",
              accountName: "",
              accountKey: "",
              containerName: "",
              filenameTemplate: `${FILENAME_TEMPLATE}csv`,
              mediaFilenameTemplate: "{{orgId}}/{{sourcePhone}}/media/{{_id}}.{{fileType}}",
            }
      }
      validate={validate}
      onSubmit={onSubmit}
      formikRef={formikContext}
    >
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.format")} />
        <FormSelect<{ value: "json" | "csv" }>
          name={"format"}
          paddingInline="none"
          options={[
            { label: "JSON", value: "json" },
            { label: "CSV", value: "csv" },
          ]}
          onChange={(selectedOption: { value: "json" | "csv" }) => onChangeFormat(selectedOption?.value)}
          defaultValue={settings?.format ? { label: settings.format.toUpperCase(), value: settings.format } : { label: "CSV", value: "csv" }}
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.account_name")} required />
        <FormTextInput name={"accountName"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.account_key")} required />
        <FormPasswordTextInput name={"accountKey"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.container_name")} required />
        <FormTextInput name={"containerName"} paddingInline="none" />
      </div>

      <CommonAzure
        filenameTemplateSelect={filenameTemplateSelect}
        formatValue={formatValue}
        settings={settings as azureStorageSharedKeySettingsFormDataType}
        formikContext={formikContext}
        selectedType="storage-shared-key-credential"
      />
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
};

type azureClientSecretSettingsFormDataTypeFields =
  | "tenantId"
  | "clientId"
  | "clientSecret"
  | "url"
  | "storeMedia"
  | "containerName"
  | "format"
  | "filenameTemplate"
  | "mediaFilenameTemplate"
  | "dek";
type azureClientSecretSettingsFormDataType = Pick<azureClientSecretSettings, azureClientSecretSettingsFormDataTypeFields> & {
  dekEnabled?: boolean;
};

const ClientSecretCredentialTab: React.FC<DestinationFormProps<"azureBlob">> = ({ settings, onSubmitForm }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<azureClientSecretSettingsFormDataType>>(null);
  const [formatValue, setFormatValue] = React.useState<"json" | "csv">(settings?.format ?? "csv");
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const [filenameTemplateSelect, setFilenameTemplateSelect] = useState<{ label: string; value: string } | undefined>();

  const validate = async (values: azureClientSecretSettingsFormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof azureClientSecretSettingsFormDataType, string>> = {};
    if (!values.format) errors.format = t("field_is_required");
    if (!values.tenantId) errors.tenantId = t("field_is_required");
    if (!values.clientId) errors.clientId = t("field_is_required");
    if (!values.clientSecret) errors.clientSecret = t("field_is_required");
    if (!values.url) errors.url = t("field_is_required");
    if (!errors.url && !getServerOrigin(values.url)) errors.url = t("invalid_url");
    if (!values.containerName) errors.containerName = t("field_is_required");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);

    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as azureClientSecretSettingsFormDataType;
    delete data.dekEnabled;
    return data;
  };

  const getSubmitData = (): AzureBlobDestination => {
    return {
      integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
      type: "azureBlob" as const,
      settings: {
        type: "client-secret-credential",
        ...getFormValues(),
        format: formatValue,
        url: getServerOrigin(getFormValues().url) ?? getFormValues().url,
      },
    };
  };

  const onSubmit = async () => {
    store.testConnection(t, getSubmitData());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getSubmitData());
    }
  };

  const onChangeFormat = (value: "json" | "csv") => {
    setFormatValue(value);
    const currentFileNameTemplateValue = formikContext.current?.values.filenameTemplate || `${FILENAME_TEMPLATE}.${value}`;
    const [currentValue] = currentFileNameTemplateValue.split(".");
    const newFileNameTemplateValue = FILENAME_TEMPLATE_OPTIONS.find((opt) => {
      const [optValue, optFormat] = opt.value.split(".");
      return optValue === currentValue && optFormat === value;
    }) || {
      label: `${FILENAME_TEMPLATE}.${value}`,
      value: `${FILENAME_TEMPLATE}.${value}`,
    };
    setFilenameTemplateSelect(newFileNameTemplateValue);
    formikContext.current?.setFieldValue("filenameTemplate", newFileNameTemplateValue?.value || `${FILENAME_TEMPLATE}.${value}`);
  };

  useEffect(() => {
    validate(getFormValues());
  }, []);

  return (
    <FormElement
      initialValues={
        settings && settings.type === "client-secret-credential"
          ? pick<azureClientSecretSettings, azureClientSecretSettingsFormDataTypeFields>(settings, [
              "tenantId",
              "clientId",
              "clientSecret",
              "url",
              "storeMedia",
              "containerName",
              "format",
              "filenameTemplate",
              "mediaFilenameTemplate",
              "dek",
            ])
          : {
              format: "csv",
              tenantId: "",
              clientId: "",
              clientSecret: "",
              containerName: "",
              url: "",
              filenameTemplate: `${FILENAME_TEMPLATE}csv`,
              mediaFilenameTemplate: "{{orgId}}/{{sourcePhone}}/media/{{_id}}.{{fileType}}",
            }
      }
      validate={validate}
      onSubmit={onSubmit}
      formikRef={formikContext}
    >
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.format")} />
        <FormSelect<{ value: "json" | "csv" }>
          name={"format"}
          paddingInline="none"
          options={[
            { label: "JSON", value: "json" },
            { label: "CSV", value: "csv" },
          ]}
          onChange={(selectedOption: { value: "json" | "csv" }) => onChangeFormat(selectedOption?.value)}
          defaultValue={
            settings?.format
              ? {
                  label: settings.format.toUpperCase(),
                  value: settings.format,
                }
              : { label: "CSV", value: "csv" }
          }
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.url")} required />
        <FormTextInput name={"url"} paddingInline="none" placeholder={"https://<account>.blob.core.windows.net"} />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.tenant_id")} required />
        <FormTextInput name={"tenantId"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.client_id")} required />
        <FormTextInput name={"clientId"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.client_secret")} required />
        <FormPasswordTextInput name={"clientSecret"} paddingInline="none" />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.container_name")} required />
        <FormTextInput name={"containerName"} paddingInline="none" />
      </div>
      <CommonAzure
        filenameTemplateSelect={filenameTemplateSelect}
        formatValue={formatValue}
        settings={settings as azureClientSecretSettingsFormDataType}
        formikContext={formikContext}
        selectedType="client-secret-credential"
      />
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
};

type azureSasUrlSettingsFormDataTypeFields = "sasUrl" | "storeMedia" | "format" | "filenameTemplate" | "mediaFilenameTemplate" | "dek";

type azureSasUrlSettingsFormDataType = Pick<azureSasUrlSettings, azureSasUrlSettingsFormDataTypeFields> & {
  dekEnabled?: boolean;
};

const SasUrlCredentialTab: React.FC<DestinationFormProps<"azureBlob">> = ({ settings, onSubmitForm }) => {
  const { t } = useTranslation();
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);
  const formikContext = useRef<FormikProps<azureSasUrlSettingsFormDataType>>(null);
  const [formatValue, setFormatValue] = React.useState<"json" | "csv">(settings?.format ?? "csv");
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const [filenameTemplateSelect, setFilenameTemplateSelect] = useState<{ label: string; value: string } | undefined>();

  const validate = async (values: azureSasUrlSettingsFormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof azureSasUrlSettingsFormDataType, string>> = {};
    if (!values.format) errors.format = t("field_is_required");
    if (!values.sasUrl) errors.sasUrl = t("field_is_required");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);

    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as azureSasUrlSettingsFormDataType;
    delete data.dekEnabled;
    return data;
  };

  const getSubmitData = (): AzureBlobDestination => {
    const formValues = getFormValues();

    return {
      integrationType: DESTINATION_INTEGRATION_TYPES.MESSAGES,
      type: "azureBlob" as const,
      settings: {
        type: "sas-url-credential",
        ...formValues,
        format: formatValue,
      },
    };
  };

  const onSubmit = async () => {
    store.testConnection(t, getSubmitData());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getSubmitData());
    }
  };

  const onChangeFormat = (value: "json" | "csv") => {
    setFormatValue(value);
    const currentFileNameTemplateValue = formikContext.current?.values.filenameTemplate || `${FILENAME_TEMPLATE}.${value}`;
    const [currentValue] = currentFileNameTemplateValue.split(".");
    const newFileNameTemplateValue = FILENAME_TEMPLATE_OPTIONS.find((opt) => {
      const [optValue, optFormat] = opt.value.split(".");
      return optValue === currentValue && optFormat === value;
    }) || {
      label: `${FILENAME_TEMPLATE}.${value}`,
      value: `${FILENAME_TEMPLATE}.${value}`,
    };
    setFilenameTemplateSelect(newFileNameTemplateValue);
    formikContext.current?.setFieldValue("filenameTemplate", newFileNameTemplateValue?.value || `${FILENAME_TEMPLATE}.${value}`);
  };

  useEffect(() => {
    validate(getFormValues());
  }, []);

  return (
    <FormElement
      initialValues={
        settings && settings.type === "sas-url-credential"
          ? pick<azureSasUrlSettings, azureSasUrlSettingsFormDataTypeFields>(settings, [
              "sasUrl",
              "storeMedia",
              "format",
              "filenameTemplate",
              "mediaFilenameTemplate",
              "dek",
            ])
          : {
              format: "csv",
              sasUrl: "",
              filenameTemplate: `${FILENAME_TEMPLATE}csv`,
              mediaFilenameTemplate: "{{orgId}}/{{sourcePhone}}/media/{{_id}}.{{fileType}}",
            }
      }
      validate={validate}
      onSubmit={onSubmit}
      formikRef={formikContext}
    >
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.format")} />
        <FormSelect<{ value: "json" | "csv" }>
          name={"format"}
          paddingInline="none"
          options={[
            { label: "JSON", value: "json" },
            { label: "CSV", value: "csv" },
          ]}
          onChange={(selectedOption: { value: "json" | "csv" }) => onChangeFormat(selectedOption?.value)}
          defaultValue={
            settings?.format
              ? {
                  label: settings.format.toUpperCase(),
                  value: settings.format,
                }
              : { label: "CSV", value: "csv" }
          }
          isClearable={false}
          required
        />
      </div>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={t("destinations_page.sas_url")} required />
        <FormPasswordTextInput name={"sasUrl"} paddingInline="none" placeholder={"https://<account>.blob.core.windows.net/<container>?<sas-token>"} />
      </div>

      <CommonAzure
        filenameTemplateSelect={filenameTemplateSelect}
        formatValue={formatValue}
        settings={settings as azureSasUrlSettingsFormDataType}
        formikContext={formikContext}
        selectedType="sas-url-credential"
      />
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
};

export const AzureBlobModalForm: React.FC<DestinationFormProps<"azureBlob">> = ({ settings, onSubmitForm, destinationType }) => {
  const [selectedTab, setSelectedTab] = useState<AvailableTabs>(
    (settings ?? { type: "storage-shared-key-credential" }).type || "storage-shared-key-credential"
  );

  const TABS: {
    key: AvailableTabs;
    value: string;
    Component: React.FC<DestinationFormProps<"azureBlob">>;
  }[] = useMemo(() => {
    return [
      {
        Component: StorageSharedKeyCredentialTab,
        key: "storage-shared-key-credential",
        value: "Storage Shared Key",
        testId: "storage-shared-key-credential-tab",
      },
      {
        Component: ClientSecretCredentialTab,
        key: "client-secret-credential",
        value: "Client Secret",
        testId: "client-secret-credential-tab",
      },
      {
        Component: SasUrlCredentialTab,
        key: "sas-url-credential",
        value: "SAS URL",
        testId: "sas-url-credential-tab",
      },
    ];
  }, []);

  const activeTab = useMemo(() => TABS.find((item) => item.key === selectedTab) ?? TABS[0], [selectedTab, TABS]);

  return (
    <>
      <ButtonSet<{ key: AvailableTabs; value: string }>
        testId={`switch-azure-blob-mode`}
        className={classNames(styles.tabs, styles.azureBlobTabs)}
        items={TABS}
        onClick={(item) => setSelectedTab(item.key)}
        activeItem={activeTab}
      />
      <activeTab.Component onSubmitForm={onSubmitForm} settings={settings} destinationType={destinationType} />
    </>
  );
};
